import {
  Box,
  Button,
  FormControl,
  FormControlLabel,
  FormGroup,
  InputBase,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  SelectChangeEvent,
  Typography,
} from '@mui/material';
import Checkbox from '@mui/material/Checkbox';
import { useEffect, useMemo, useReducer, useState } from 'react';
import { produce } from 'immer';

export interface Option {
  value: string | number;
  text: string;
}

export interface DropdownSelect {
  label: string;
  labelId: string;
  categoryName: string;
  options: Option[];
}

export interface CheckSelect {
  label: string;
  categoryName: string;
}

export type FilterState = {
  [key: string]: number | string | boolean | null;
};

enum FilterStateActionType {
  Init,
  Update,
  Reset,
}

interface FilterStateInitAction {
  type: FilterStateActionType.Init;
  payload: {
    DropdownSelectData: DropdownSelect[];
    CheckSelectData: CheckSelect[];
  };
}

interface FilterStateUpdateAction {
  type: FilterStateActionType.Update;
  payload: {
    categoryName: string;
    value: boolean | string | number | null;
  };
}

interface FilterStateResetAction {
  type: FilterStateActionType.Reset;
}

type FilterStateAction =
  | FilterStateInitAction
  | FilterStateUpdateAction
  | FilterStateResetAction;

const FilterStateReducer = (state: FilterState, action: FilterStateAction) => {
  const { type } = action;
  if (type === FilterStateActionType.Init) {
    const { DropdownSelectData, CheckSelectData } = action.payload;
    DropdownSelectData.forEach(d => {
      state[d.categoryName] = null;
    });
    CheckSelectData.forEach(d => {
      state[d.categoryName] = false;
    });
    state['title'] = '';
  } else if (type === FilterStateActionType.Update) {
    const payload = action.payload;
    state[payload.categoryName] = payload.value;
  } else if (type === FilterStateActionType.Reset) {
    Object.keys(state).forEach(k => {
      state[k] = null;
    });
  }
  return state;
};

interface FilterProps {
  DropdownSelectData: DropdownSelect[];
  CheckSelectData: CheckSelect[];
  hasSearchBox?: boolean;
  updateFilterState: React.Dispatch<React.SetStateAction<FilterState>>;
  useFixedPosition?: boolean;
}

const Filter: React.FC<FilterProps> = ({
  DropdownSelectData,
  CheckSelectData,
  hasSearchBox = true,
  updateFilterState,
  useFixedPosition = true,
}) => {
  const [filterState, dispatch] = useReducer(produce(FilterStateReducer), {});
  const [dataInitialized, setDataInitialized] = useState<boolean>(false);
  useEffect(() => {
    if (dataInitialized) return;
    dispatch({
      type: FilterStateActionType.Init,
      payload: {
        DropdownSelectData,
        CheckSelectData,
      },
    });
    setDataInitialized(true);
  }, [DropdownSelectData, CheckSelectData]);

  const filterConfirmHandler = () => {
    // console.log(filterState);
    updateFilterState(filterState);
  };

  const filterResetHandler = () => {
    updateFilterState({});
    dispatch({
      type: FilterStateActionType.Reset,
    });
    // console.log(filterState)
  };

  const dropDowns = useMemo(() => {
    return DropdownSelectData.map((d,i) => {
      return (
        <FormControl fullWidth sx={{ marginTop: '24px' }} key={i}>
          <InputLabel id={d.labelId}>{d.label}</InputLabel>
          <Select
            labelId={d.labelId}
            label={d.label}
            value={filterState[d.categoryName] ? filterState[d.categoryName] as number | string : -1}
            onChange={(e: SelectChangeEvent<string | number>) => {
              dispatch({
                type: FilterStateActionType.Update,
                payload: {
                  categoryName: d.categoryName,
                  value: e.target.value,
                },
              });
            }}
          >
            {d.options.map((option, i) => {
              return <MenuItem value={option.value} key={i}>{option.text}</MenuItem>;
            })}
          </Select>
        </FormControl>
      );
    });
  }, [filterState, DropdownSelectData]);

  return (
    <Box sx={{ 
      marginLeft: '8px', 
      marginRight: useFixedPosition ? '80px' : '8px', 
      position: useFixedPosition ? 'fixed' : 'relative' 
    }}>
      <Box className="filter">
        <Typography
          variant="h5"
          color="var(--turquoise-text)"
          fontWeight={'bold'}
        >
          Filters
        </Typography>
        {hasSearchBox && (
          <Box className="searchBar" sx={{ width: '100%', marginTop: '16px' }}>
            <Paper component="form" sx={{ padding: '10px 14px', boxSizing: 'border-box' }}>
              <InputBase
                placeholder="Search By Name"
                type="text"
                fullWidth
                value={filterState['title'] || ''}
                onChange={e => {
                  dispatch({
                    type: FilterStateActionType.Update,
                    payload: {
                      categoryName: 'title',
                      value: e.target.value,
                    },
                  });
                }}
              />
            </Paper>
          </Box>
        )}
        {dropDowns}
        <FormGroup sx={{ marginTop: '16px', marginLeft: '16px' }}>
          {CheckSelectData.map((d, i) => {
            return (
              <FormControlLabel
                key={i}
                control={
                  <Checkbox
                    sx={{ color: 'var(--job-status-green)' }}
                    checked={filterState[d.categoryName] ? true : false}
                    onChange={e => {
                      dispatch({
                        type: FilterStateActionType.Update,
                        payload: {
                          categoryName: d.categoryName,
                          value: e.target.checked,
                        },
                      });
                    }}
                  />
                }
                label={d.label}
              />
            );
          })}
        </FormGroup>
      </Box>
      <Button
        className="viewDetailsButton"
        variant="contained"
        sx={{
          backgroundColor: 'var(--ttg-black)',
          '&:hover': {
            backgroundColor: 'var(--ttg-green)',
          },
          margin: '10px 5px 0 0',
          padding: '10px',
        }}
        onClick={filterConfirmHandler}
      >
        Confirm
      </Button>
      <Button
        className="viewDetailsButton"
        variant="contained"
        sx={{
          backgroundColor: 'var(--ttg-black)',
          '&:hover': {
            backgroundColor: 'var(--ttg-green)',
          },
          margin: '10px 5px 0 5px',
          padding: '10px',
        }}
        onClick={filterResetHandler}
      >
        Reset
      </Button>
    </Box>
  );
};

export default Filter;
